import { type MappingValue } from 'src/modules/automations/components/molecules/create-trigger-modal/mapping-row';
import {
  type TCTemplateRecord,
  type WorkflowTriggerInputMappingInstruction,
} from 'src/api/generated';
import {
  buildEventPathOptions,
  getFieldExpectedLiteralTypes,
  getPropertyKeysFromSchema,
  getRequiredFieldsFromSchema,
  isTemplateDefinition,
} from './template-definition.util';
import {
  type LiteralType,
  type TemplateDefinition,
} from './template-definition.type';

export const decodeMappingValue = (triggerDataPath: string): MappingValue => {
  if (triggerDataPath.startsWith('$literal:')) {
    const [, literalType, ...rest] = triggerDataPath.split(':');
    const joined = rest.join(':');
    const lt = (literalType as LiteralType) || 'string';
    return {
      kind: 'literal',
      literalType: lt,
      value: joined ?? '',
    };
  }
  return { kind: 'event', path: triggerDataPath };
};

export const encodeLiteral = (literalType: LiteralType, value: string) => {
  if (literalType === 'null') return '$literal:null:';
  return `$literal:${literalType}:${value}`;
};

export const buildDefaultMapping = (
  template: TCTemplateRecord | null | undefined,
  presetMapping?: Record<string, MappingValue>
): Record<string, MappingValue> => {
  if (!template) return {};
  const next: Record<string, MappingValue> = { ...(presetMapping ?? {}) };

  template.trigger?.inputMapping?.instructions?.forEach(
    (instr: WorkflowTriggerInputMappingInstruction) => {
      if (!next[instr.workflowInputField]) {
        next[instr.workflowInputField] = decodeMappingValue(
          instr.triggerDataPath
        );
      }
    }
  );

  return next;
};

export const buildInstructions = (
  fieldKeys: string[],
  mapping: Record<string, MappingValue>
) => {
  const instructions: Array<{
    workflowInputField: string;
    triggerDataPath: string;
  }> = [];
  for (const key of fieldKeys) {
    const m = mapping[key];
    if (!m || m.kind === 'empty') continue;
    if (m.kind === 'event') {
      if (m.path.trim()) {
        instructions.push({
          workflowInputField: key,
          triggerDataPath: m.path.trim(),
        });
      }
    } else if (m.kind === 'literal') {
      instructions.push({
        workflowInputField: key,
        triggerDataPath: encodeLiteral(m.literalType, m.value),
      });
    }
  }
  return instructions;
};

export const areRequiredFieldsMapped = (
  mapping: Record<string, MappingValue>,
  requiredFields: Set<string>
) => {
  for (const key of requiredFields) {
    const m = mapping[key];
    if (!m || m.kind === 'empty') return false;
    if (m.kind === 'event' && !m.path.trim()) return false;
    if (m.kind === 'literal' && m.literalType !== 'null' && !m.value.trim()) {
      return false;
    }
  }
  return true;
};

export const getTemplateSchemaMetadata = (
  template: TCTemplateRecord | null
): {
  templateDefinition?: TemplateDefinition;
  fieldKeys: string[];
  requiredFields: Set<string>;
  fieldExpectedLiteralTypes: Record<string, LiteralType>;
} => {
  if (!template || !isTemplateDefinition(template.definition)) {
    return {
      templateDefinition: undefined,
      fieldKeys: [],
      requiredFields: new Set<string>(),
      fieldExpectedLiteralTypes: {} as Record<string, LiteralType>,
    };
  }

  const templateDefinition = template.definition;
  const inputSchema = templateDefinition.inputSchema;

  return {
    templateDefinition,
    fieldKeys: getPropertyKeysFromSchema(inputSchema),
    requiredFields: getRequiredFieldsFromSchema(inputSchema),
    fieldExpectedLiteralTypes: getFieldExpectedLiteralTypes(inputSchema),
  };
};

export const buildEventOptionsFromTriggerSchema = (schema: unknown) =>
  buildEventPathOptions(schema);
